import { Component, computed, effect, inject, input, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CheckboxModule } from 'primeng/checkbox';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { SkeletonModule } from 'primeng/skeleton';
import { Api } from '@services/api.service';
import { AuthService } from '@services/auth.service';
import { ToastService } from '@services/toast.service';
import { IOrgUserView } from '@common-interfaces/org-user';
import {
    AvailableUserScopes,
    availableUserScopes,
    canGrantScope,
    canManageTarget,
    presetForScopes,
    RolePreset,
    rolePresets
} from '../../../../../../../../../../common/common-types/user-scopes';
import { PageHeaderComponent } from '../../../../../components/shared/page-header/page-header.component';
import { SectionCardComponent } from '../../../../../components/shared/section-card/section-card.component';
import { SaveBarComponent } from '../../../../../components/shared/save-bar/save-bar.component';
import { debugLogger } from '@common-helpers/debug-logger';

const PRESET_ICONS: Record<RolePreset, string> = {
    OWNER: 'ph-crown',
    MARKETING: 'ph-megaphone',
    CUSTOMER_SERVICE: 'ph-headset',
    FINANCE: 'ph-coins'
};

const SCOPE_ICONS: Record<AvailableUserScopes, string> = {
    super: 'ph-crown',
    crm: 'ph-megaphone',
    customers: 'ph-address-book',
    triggers: 'ph-lightning',
    users: 'ph-user-gear',
    payments: 'ph-credit-card'
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

@Component({
    selector: 'user-editor',
    imports: [
        FormsModule,
        TranslatePipe,
        ButtonModule,
        InputTextModule,
        CheckboxModule,
        ToggleSwitchModule,
        SkeletonModule,
        PageHeaderComponent,
        SectionCardComponent,
        SaveBarComponent
    ],
    templateUrl: './user-editor.component.html'
})
export class UserEditorComponent {
    private api = inject(Api);
    private auth = inject(AuthService);
    private router = inject(Router);
    private toast = inject(ToastService);
    private translate = inject(TranslateService);
    private confirm = inject(ConfirmationService);

    /** Route param; absent on `admin/users/new`. */
    userId = input<string>();

    isNew = computed(() => !this.userId());
    orgId = this.auth.organizationId;
    venues = this.auth.clientsInOrganization;

    loading = signal(false);
    saving = signal(false);
    loadError = signal(false);
    showAdvanced = signal(false);
    submitted = signal(false);

    firstName = signal('');
    lastName = signal('');
    email = signal('');
    phone = signal('');
    disabled = signal(false);
    scopes = signal<AvailableUserScopes[]>([]);
    venueIds = signal<string[]>([]);

    /** Scopes as loaded, used to decide whether the current user may manage this user at all. */
    private loadedScopes = signal<AvailableUserScopes[]>([]);
    /** The user as last loaded or saved, for dirty tracking and Discard. */
    private loaded = signal<IOrgUserView | null>(null);

    private formState = computed(() =>
        JSON.stringify([
            this.firstName(),
            this.lastName(),
            this.phone(),
            this.disabled(),
            [...this.scopes()].sort(),
            [...this.venueIds()].sort()
        ])
    );
    private savedState = signal('');
    dirty = computed(() => !this.isNew() && this.formState() !== this.savedState());

    actor = this.auth.currentUser;
    isSelf = computed(() => {
        const me = this.actor() as { id?: string; _id?: string } | null;
        return !this.isNew() && !!me && [me.id, me._id].includes(this.userId());
    });
    canManage = computed(() => this.isNew() || canManageTarget(this.actor(), this.orgId(), this.loadedScopes()));
    roleLocked = computed(() => !this.canManage() || this.isSelf());

    fullName = computed(() => `${this.firstName()} ${this.lastName()}`.trim());
    activePreset = computed(() => presetForScopes(this.scopes()));

    presets = computed(() =>
        (Object.keys(rolePresets) as RolePreset[]).map((preset) => ({
            key: preset,
            icon: PRESET_ICONS[preset],
            scopes: rolePresets[preset] as readonly AvailableUserScopes[],
            grantable: (rolePresets[preset] as readonly AvailableUserScopes[]).every((s) => canGrantScope(this.actor(), this.orgId(), s))
        }))
    );

    // `super` is chosen through the Owner preset; the advanced list covers the individual areas.
    scopeOptions = computed(() =>
        availableUserScopes
            .filter((scope) => scope !== 'super')
            .map((scope) => ({ key: scope, icon: SCOPE_ICONS[scope], grantable: canGrantScope(this.actor(), this.orgId(), scope) }))
    );

    errors = computed(() => ({
        firstName: !this.firstName().trim(),
        lastName: !this.lastName().trim(),
        email: !EMAIL_PATTERN.test(this.email().trim())
    }));
    valid = computed(() => !Object.values(this.errors()).some(Boolean));

    allVenuesSelected = computed(() => this.venues().length > 0 && this.venues().every((v) => this.venueIds().includes(v._id)));

    constructor() {
        effect(() => {
            const id = this.userId();
            const orgId = this.orgId();
            if (id && orgId) {
                untracked(() => this.load(id));
            }
        });
    }

    applyPreset(preset: RolePreset): void {
        if (this.roleLocked()) {
            return;
        }
        this.scopes.set([...rolePresets[preset]]);
    }

    hasScope(scope: AvailableUserScopes): boolean {
        return this.scopes().includes(scope);
    }

    toggleScope(scope: AvailableUserScopes): void {
        if (this.roleLocked()) {
            return;
        }
        // Owner already covers everything, so picking a single area leaves the Owner role.
        const current: AvailableUserScopes[] = this.scopes().filter((s) => s !== 'super');
        this.scopes.set(current.includes(scope) ? current.filter((s) => s !== scope) : [...current, scope]);
    }

    toggleVenue(id: string): void {
        this.venueIds.update((ids) => (ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id]));
    }

    toggleAllVenues(): void {
        this.venueIds.set(this.allVenuesSelected() ? [] : this.venues().map((v) => v._id));
    }

    cancel(): void {
        this.router.navigate(['/organizations', this.orgId(), 'admin', 'users']);
    }

    async save(): Promise<void> {
        this.submitted.set(true);
        if (!this.valid() || this.saving()) {
            return;
        }

        this.saving.set(true);
        try {
            const user = { firstName: this.firstName(), lastName: this.lastName(), email: this.email().trim(), phone: this.phone() };

            if (this.isNew()) {
                const res = await this.api.currentorganization().put<IOrgUserView>('/users', {
                    user,
                    scopes: this.scopes(),
                    associatedClients: this.venueIds()
                });
                if (res.success) {
                    // New accounts get a reset link so they can choose their own password.
                    this.api
                        .fromUrl('/api/reset-password')
                        .post('/', { username: res.data.email })
                        .catch((error) => debugLogger.error('Failed to send invite email', error));
                    this.toast.showSuccess(this.translate.instant('USER_INVITED', { email: res.data.email }));
                    this.router.navigate(['/organizations', this.orgId(), 'admin', 'users', res.data._id], { replaceUrl: true });
                }
                return;
            }

            const res = await this.api.currentorganization().patch<IOrgUserView>(`/users/${this.userId()}`, {
                updatedUser: { ...user, disabled: this.disabled() },
                ...(this.roleLocked() ? {} : { scopes: this.scopes(), associatedClients: this.venueIds() })
            });
            if (res.success) {
                this.apply(res.data);
                this.toast.showSuccess(this.translate.instant('USER_SAVED'));
            }
        } catch (error) {
            this.showError(error);
        } finally {
            this.saving.set(false);
        }
    }

    async sendPasswordReset(): Promise<void> {
        try {
            await this.api.fromUrl('/api/reset-password').post('/', { username: this.email() });
            this.toast.showSuccess(this.translate.instant('PASSWORD_RESET_SENT'));
        } catch (error) {
            this.showError(error);
        }
    }

    confirmRemove(): void {
        this.confirm.confirm({
            header: this.translate.instant('REMOVE_USER_TITLE'),
            message: this.translate.instant('REMOVE_USER_TEXT', { name: this.fullName() || this.email() }),
            icon: 'ph ph-warning',
            acceptLabel: this.translate.instant('REMOVE'),
            rejectLabel: this.translate.instant('CANCEL'),
            acceptButtonStyleClass: 'p-button-danger',
            rejectButtonStyleClass: 'p-button-text',
            accept: () => this.remove()
        });
    }

    private async remove(): Promise<void> {
        try {
            const res = await this.api.currentorganization().delete(`/users/${this.userId()}`);
            if (res.success) {
                this.toast.showSuccess(this.translate.instant('USER_REMOVED'));
                this.cancel();
            }
        } catch (error) {
            this.showError(error);
        }
    }

    private async load(id: string): Promise<void> {
        this.loading.set(true);
        this.loadError.set(false);
        try {
            const res = await this.api.currentorganization().get<IOrgUserView>(`/users/${id}`);
            if (res.success) {
                this.apply(res.data);
            } else {
                this.loadError.set(true);
            }
        } catch (error) {
            debugLogger.error('Failed to load user', error);
            this.loadError.set(true);
        } finally {
            this.loading.set(false);
        }
    }

    private apply(user: IOrgUserView): void {
        this.firstName.set(user.firstName);
        this.lastName.set(user.lastName);
        this.email.set(user.email);
        this.phone.set(user.phone);
        this.disabled.set(user.disabled);
        this.scopes.set([...user.scopes]);
        this.loadedScopes.set([...user.scopes]);
        this.venueIds.set([...user.associatedClients]);
        this.showAdvanced.set(!presetForScopes(user.scopes) && user.scopes.length > 0);
        this.loaded.set(user);
        this.savedState.set(this.formState());
    }

    discard(): void {
        const user = this.loaded();
        if (user) {
            this.apply(user);
        }
    }

    private showError(error: unknown): void {
        const code = error instanceof HttpErrorResponse ? error.error?.error : null;
        const key = code ? `ERR_${code}` : 'ERR_GENERIC';
        const message = this.translate.instant(key);
        this.toast.showError(message === key ? this.translate.instant('ERR_GENERIC') : message);
        debugLogger.error('User administration request failed', error);
    }
}
